import React, { useEffect, useRef, useState } from 'react';
import { cx } from 'class-variance-authority';

import { ProductDocument } from '@util/types/firestore/products';
import { linkifyHtml, linkifyPlainText } from '@util/linkifyHtml';

type Props = {
  product: ProductDocument;
};

const ProductDescriptionCard: React.FC<Props> = ({ product }) => {
  const [readingMore, setReadingMore] = useState(false);

  const [overflowActive, setOverflowActive] = useState(false);
  const overflowingText = useRef<HTMLParagraphElement | null>(null);

  const checkOverflow = (textContainer: HTMLSpanElement | null): boolean => {
    if (textContainer)
      return (
        textContainer.offsetHeight < textContainer.scrollHeight ||
        textContainer.offsetWidth < textContainer.scrollWidth
      );
    return false;
  };

  useEffect(() => {
    setOverflowActive(checkOverflow(overflowingText.current));
  }, [overflowActive]);

  return (
    <div className="formatquill">
      {product.description_html || product.description ? (
        <>
          <div
            ref={overflowingText}
            className={cx(
              'overflow-x-auto overflow-y-hidden xl:min-w-[32rem]',
              readingMore
                ? 'h-fit'
                : 'line-clamp-4 max-h-[10rem] overflow-hidden'
            )}
          >
            {product.description_html ? (
              // Display HTML content
              <div
                className="mb-5"
                dangerouslySetInnerHTML={{
                  __html: linkifyHtml(product.description_html),
                }}
              />
            ) : (
              // Display plain text content with preserved spacing (URLs linkified)
              <div
                style={{ whiteSpace: 'pre-wrap' }}
                className="mb-5"
                dangerouslySetInnerHTML={{
                  __html: linkifyPlainText(product.description ?? ''),
                }}
              />
            )}
          </div>

          {overflowActive && (
            <div className="pt-5">
              {!readingMore ? (
                <button
                  onClick={() => setReadingMore(true)}
                  className="text-brand-secondary"
                >
                  Read more
                </button>
              ) : (
                <button
                  onClick={() => setReadingMore(false)}
                  className="text-brand-secondary"
                >
                  Read less
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <span className="italic">No description available</span>
      )}
    </div>
  );
};

export default ProductDescriptionCard;
